var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { CellType, MatrixColumnTotalDisplayStyle, MatrixColumnTotalType, QuestionsOnPageMode, ShowQuestionNumbers, SurveyElementTypeEnum, SurveyInputTypeEnum, TextUpdateMode, NormalizedClearInvisibleValues, CheckErrorsMode, } from "../types/forms";
import { evaluateElementConditions, evaluateValueExpression, validateValidatorsPageElements, verifyElementCondition, } from "./elements-condition";
import { COMMENT_FIELD_SUFFIX, compareTwoArraysEqual, compareTwoValuesEquals, getElementValueKey, isNumericValue, isValueFilled, MATRIX_DYNAMIC_DEFAULT_ROWS, MATRIX_SEPARATOR_CHARACTER, parseFlexibleNumericValue, reorderChoices, } from "./helpers";
export var removePageWithoutElements = function (data) {
    return __assign(__assign({}, data), { pages: data.pages.filter(function (page) { return page.elements && page.elements.length > 0; }) });
};
export var resolveTextUpdateMode = function (element, formData) {
    var _a, _b;
    return ((_b = (_a = element.textUpdateMode) !== null && _a !== void 0 ? _a : formData.textUpdateMode) !== null && _b !== void 0 ? _b : TextUpdateMode.ON_BLUR);
};
export var resolveStoreOthersAsComment = function (element, surveyStoreOthersAsComment) {
    var _a;
    if (element.showCommentArea)
        return false;
    return (_a = element.storeOthersAsComment) !== null && _a !== void 0 ? _a : surveyStoreOthersAsComment;
};
export var normalizeStoreOthersAsComment = function (value) { return value !== null && value !== void 0 ? value : true; };
export var normalizeClearInvisibleValues = function (value) {
    if (value === true)
        return NormalizedClearInvisibleValues.ON_COMPLETE;
    if (value === false)
        return NormalizedClearInvisibleValues.NONE;
    return value !== null && value !== void 0 ? value : NormalizedClearInvisibleValues.ON_COMPLETE;
};
export var isValueEmpty = function (value) {
    if (Array.isArray(value)) {
        return value.length === 0 || value.every(function (item) { return isValueEmpty(item); });
    }
    if (typeof value === "object" && value !== null) {
        return Object.values(value).every(function (item) {
            return isValueEmpty(item);
        });
    }
    if (typeof value === "string") {
        return value.trim().length === 0;
    }
    return value === undefined || value === null;
};
export var flattenPageElements = function (elements) {
    var _a;
    var result = [];
    for (var _i = 0, elements_1 = elements; _i < elements_1.length; _i++) {
        var element = elements_1[_i];
        if (element.type === SurveyElementTypeEnum.PANEL &&
            ((_a = element.elements) === null || _a === void 0 ? void 0 : _a.length)) {
            result.push.apply(result, flattenPageElements(element.elements));
            continue;
        }
        result.push(element);
    }
    return result;
};
export var isValueProducingElement = function (element) {
    return element.type !== SurveyElementTypeEnum.HTML &&
        element.type !== SurveyElementTypeEnum.IMAGE;
};
/**
 * Equivalente a `Question.hasInput` do SurveyJS: elementos que recebem resposta
 * do usuário. `expression` produz valor, mas é somente leitura — por isso fica
 * fora de obrigatoriedade, progresso por perguntas e avanço automático.
 */
export var isInputElement = function (element) {
    return isValueProducingElement(element) &&
        element.type !== SurveyElementTypeEnum.EXPRESSION;
};
export var isElementEnabledAndVisible = function (element, formValues, elementsTypeInfoByName, pageEnabledFallback) {
    var _a = evaluateElementConditions(element, formValues, elementsTypeInfoByName, pageEnabledFallback), isVisible = _a.isVisible, isEnabled = _a.isEnabled;
    return isVisible && isEnabled;
};
export var isElementVisible = function (element, formValues, elementsTypeInfoByName) {
    return evaluateElementConditions(element, formValues, elementsTypeInfoByName, true)
        .isVisible;
};
export var collectEnabledVisibleElements = function (elements, formValues, elementsTypeInfoByName, ancestorsEnabled) {
    var _a;
    var result = [];
    for (var _i = 0, elements_2 = elements; _i < elements_2.length; _i++) {
        var element = elements_2[_i];
        var _b = evaluateElementConditions(element, formValues, elementsTypeInfoByName, ancestorsEnabled), isVisible = _b.isVisible, isEnabled = _b.isEnabled;
        if (!isVisible) {
            continue;
        }
        if (element.type === SurveyElementTypeEnum.PANEL) {
            result.push.apply(result, collectEnabledVisibleElements((_a = element.elements) !== null && _a !== void 0 ? _a : [], formValues, elementsTypeInfoByName, isEnabled));
            continue;
        }
        if (isEnabled) {
            result.push(element);
        }
    }
    return result;
};
var hasAnyVisibleElement = function (elements, formValues, elementsTypeInfoByName) {
    return elements.some(function (element) {
        var _a;
        if (!isElementVisible(element, formValues, elementsTypeInfoByName)) {
            return false;
        }
        if (element.type === SurveyElementTypeEnum.PANEL) {
            return hasAnyVisibleElement((_a = element.elements) !== null && _a !== void 0 ? _a : [], formValues, elementsTypeInfoByName);
        }
        return true;
    });
};
export var pageHasVisibleElement = function (page, formValues, elementsTypeInfoByName) {
    return hasAnyVisibleElement(page.elements, formValues, elementsTypeInfoByName);
};
export var usesCommentKey = function (element, surveyStoreOthersAsComment) {
    return !!element.showCommentArea ||
        (!!element.showOtherItem &&
            resolveStoreOthersAsComment(element, surveyStoreOthersAsComment));
};
export var getInvisibleElementValueKeys = function (pages, formValues, elementsTypeInfoByName, surveyStoreOthersAsComment) {
    if (surveyStoreOthersAsComment === void 0) { surveyStoreOthersAsComment = true; }
    var keys = [];
    var walk = function (elements, ancestorsVisible) {
        elements.forEach(function (element) {
            var _a;
            var ownVisible = isElementVisible(element, formValues, elementsTypeInfoByName);
            var effectiveVisible = ancestorsVisible && ownVisible;
            if (element.type === SurveyElementTypeEnum.PANEL &&
                ((_a = element.elements) === null || _a === void 0 ? void 0 : _a.length)) {
                walk(element.elements, effectiveVisible);
                return;
            }
            if (!effectiveVisible) {
                var valueKey = getElementValueKey(element);
                keys.push(valueKey);
                if (usesCommentKey(element, surveyStoreOthersAsComment)) {
                    keys.push(getCommentFieldName(valueKey));
                }
            }
        });
    };
    pages.forEach(function (page) {
        return walk(page.elements, isPageVisible(page, formValues, elementsTypeInfoByName));
    });
    return keys;
};
export var applyClearInvisibleValues = function (data, mode, invisibleValueKeys) {
    if (mode === NormalizedClearInvisibleValues.NONE) {
        return data;
    }
    var result = __assign({}, data);
    invisibleValueKeys.forEach(function (key) {
        delete result[key];
    });
    return result;
};
export var isPageVisible = function (page, formValues, elementsTypeInfoByName) {
    var _a;
    var isVisibleByCondition = page.visibleIf
        ? verifyElementCondition(page.visibleIf, formValues, elementsTypeInfoByName)
        : (_a = page.visible) !== null && _a !== void 0 ? _a : true;
    if (!isVisibleByCondition) {
        return false;
    }
    return pageHasVisibleElement(page, formValues, elementsTypeInfoByName);
};
export var isPageEnabled = function (page, formValues, elementsTypeInfoByName) {
    if (!page.enableIf) {
        return !page.readOnly;
    }
    return verifyElementCondition(page.enableIf, formValues, elementsTypeInfoByName);
};
export var isPageRequired = function (page, formValues, elementsTypeInfoByName) {
    if (!page.requiredIf) {
        return false;
    }
    if (!isPageEnabled(page, formValues, elementsTypeInfoByName)) {
        return false;
    }
    return verifyElementCondition(page.requiredIf, formValues, elementsTypeInfoByName);
};
export var hasPageAtLeastOneFilledElement = function (page, formValues, elementsTypeInfoByName) {
    var pageEnabledFallback = isPageEnabled(page, formValues, elementsTypeInfoByName);
    return collectEnabledVisibleElements(page.elements, formValues, elementsTypeInfoByName, pageEnabledFallback).some(function (el) {
        return isInputElement(el) && isValueFilled(formValues[getElementValueKey(el)]);
    });
};
export var findUnansweredRequiredPanel = function (page, formValues, elementsTypeInfoByName) {
    var pageEnabledFallback = isPageEnabled(page, formValues, elementsTypeInfoByName);
    return page.elements.find(function (element) {
        var _a;
        if (element.type !== SurveyElementTypeEnum.PANEL || !element.requiredIf) {
            return false;
        }
        var _b = evaluateElementConditions(element, formValues, elementsTypeInfoByName, pageEnabledFallback), isVisible = _b.isVisible, isEnabled = _b.isEnabled;
        if (!isVisible || !isEnabled) {
            return false;
        }
        var isRequired = verifyElementCondition(element.requiredIf, formValues, elementsTypeInfoByName);
        if (!isRequired) {
            return false;
        }
        return !hasPageAtLeastOneFilledElement({
            name: element.name,
            title: element.title,
            readOnly: element.readOnly,
            enableIf: element.enableIf,
            elements: (_a = element.elements) !== null && _a !== void 0 ? _a : [],
        }, formValues, elementsTypeInfoByName);
    });
};
var filterElementsToAnswered = function (elements, formValues, elementsTypeInfoByName) {
    var _a;
    var result = [];
    for (var _i = 0, elements_3 = elements; _i < elements_3.length; _i++) {
        var element = elements_3[_i];
        if (!isElementVisible(element, formValues, elementsTypeInfoByName)) {
            continue;
        }
        if (element.type === SurveyElementTypeEnum.PANEL &&
            ((_a = element.elements) === null || _a === void 0 ? void 0 : _a.length)) {
            var filteredChildren = filterElementsToAnswered(element.elements, formValues, elementsTypeInfoByName);
            if (filteredChildren.length > 0) {
                result.push(__assign(__assign({}, element), { elements: filteredChildren }));
            }
            continue;
        }
        if (!isValueEmpty(formValues[getElementValueKey(element)])) {
            result.push(element);
        }
    }
    return result;
};
export var getAnsweredPageElements = function (page, formValues, elementsTypeInfoByName) {
    return filterElementsToAnswered(page.elements, formValues, elementsTypeInfoByName);
};
export var pickFormValuesFromPages = function (data, pages, surveyStoreOthersAsComment) {
    if (surveyStoreOthersAsComment === void 0) { surveyStoreOthersAsComment = true; }
    var allowedNames = new Set();
    for (var _i = 0, pages_1 = pages; _i < pages_1.length; _i++) {
        var page = pages_1[_i];
        for (var _a = 0, _b = flattenPageElements(page.elements); _a < _b.length; _a++) {
            var el = _b[_a];
            if (!isValueProducingElement(el))
                continue;
            var elementKeyValue = getElementValueKey(el);
            allowedNames.add(elementKeyValue);
            if (usesCommentKey(el, surveyStoreOthersAsComment)) {
                allowedNames.add(getCommentFieldName(elementKeyValue));
            }
        }
    }
    var result = {};
    for (var _c = 0, _d = Object.keys(data); _c < _d.length; _c++) {
        var key = _d[_c];
        if (allowedNames.has(key)) {
            result[key] = data[key];
        }
    }
    return injectMatrixColumnTotals(injectMatrixExpressionCells(result, pages, data), pages);
};
export var injectMatrixColumnTotals = function (payload, pages) {
    var _a;
    var result = __assign({}, payload);
    for (var _i = 0, pages_2 = pages; _i < pages_2.length; _i++) {
        var page = pages_2[_i];
        for (var _b = 0, _c = flattenPageElements(page.elements); _b < _c.length; _b++) {
            var el = _c[_b];
            if (el.type !== SurveyElementTypeEnum.MATRIXDROPDOWN &&
                el.type !== SurveyElementTypeEnum.MATRIXDYNAMIC) {
                continue;
            }
            var columns = ((_a = el.columns) !== null && _a !== void 0 ? _a : []);
            var totalColumns = columns.filter(function (col) { return col.totalType !== undefined; });
            if (totalColumns.length === 0)
                continue;
            var matrixValue = payload[getElementValueKey(el)];
            if (!matrixValue || typeof matrixValue !== "object")
                continue;
            var totals = {};
            var _loop_1 = function (col) {
                var colValues = Object.values(matrixValue).map(function (row) { return row[col.name]; });
                totals[col.name] = computeColumnTotal(colValues, col.totalType);
            };
            for (var _d = 0, totalColumns_1 = totalColumns; _d < totalColumns_1.length; _d++) {
                var col = totalColumns_1[_d];
                _loop_1(col);
            }
            result["".concat(getElementValueKey(el), "-total")] = totals;
        }
    }
    return result;
};
export var getRequiredFieldNamesForPage = function (page, formValues, elementsTypeInfoByName) {
    var pageEnabledFallback = isPageEnabled(page, formValues, elementsTypeInfoByName);
    return collectEnabledVisibleElements(page.elements, formValues, elementsTypeInfoByName, pageEnabledFallback)
        .filter(isInputElement)
        .filter(function (el) {
        if (el.requiredIf) {
            return verifyElementCondition(el.requiredIf, formValues, elementsTypeInfoByName);
        }
        return el.isRequired === true;
    })
        .map(function (el) { return getElementValueKey(el); });
};
export var getFillableFieldNamesForPage = function (page, formValues, elementsTypeInfoByName) {
    var pageEnabledFallback = isPageEnabled(page, formValues, elementsTypeInfoByName);
    return collectEnabledVisibleElements(page.elements, formValues, elementsTypeInfoByName, pageEnabledFallback)
        .filter(isInputElement)
        .map(function (element) { return getElementValueKey(element); });
};
export var shufflePageElements = function (elements) {
    var _a;
    var shuffledElements = __spreadArray([], elements, true);
    for (var index = shuffledElements.length - 1; index > 0; index -= 1) {
        var randomIndex = Math.floor(Math.random() * (index + 1));
        _a = [
            shuffledElements[randomIndex],
            shuffledElements[index],
        ], shuffledElements[index] = _a[0], shuffledElements[randomIndex] = _a[1];
    }
    return shuffledElements.map(function (element) {
        var _a;
        return element.type === SurveyElementTypeEnum.PANEL && ((_a = element.elements) === null || _a === void 0 ? void 0 : _a.length)
            ? __assign(__assign({}, element), { elements: shufflePageElements(element.elements) }) : element;
    });
};
var removeInvisibleElements = function (elements) {
    return elements
        .filter(function (element) { return element.visible !== false; })
        .map(function (element) {
        var _a;
        return element.type === SurveyElementTypeEnum.PANEL && ((_a = element.elements) === null || _a === void 0 ? void 0 : _a.length)
            ? __assign(__assign({}, element), { elements: removeInvisibleElements(element.elements) }) : element;
    });
};
export var handleRemoveInvisibleElements = function (data) { return (__assign(__assign({}, data), { pages: data.pages.map(function (page) { return (__assign(__assign({}, page), { elements: removeInvisibleElements(page.elements) })); }) })); };
export var handleApplyFormElementsShuffle = function (data) {
    if (!data.questionsOrder) {
        return data;
    }
    return __assign(__assign({}, data), { pages: data.pages.map(function (page) { return (__assign(__assign({}, page), { elements: shufflePageElements(page.elements) })); }) });
};
export var handleApplyQuestionNumbers = function (data) {
    var questionNumbersMode = data.showQuestionNumbers;
    if (questionNumbersMode === ShowQuestionNumbers.OFF) {
        return data;
    }
    var applyNumbersToElements = function (elements, getNextNumber) {
        return elements.map(function (element) {
            var _a;
            if (element.type === SurveyElementTypeEnum.PANEL &&
                ((_a = element.elements) === null || _a === void 0 ? void 0 : _a.length)) {
                return __assign(__assign({}, element), { elements: applyNumbersToElements(element.elements, getNextNumber) });
            }
            if (!isValueProducingElement(element)) {
                return element;
            }
            var number = getNextNumber();
            return __assign(__assign({}, element), { title: "".concat(number, ". ").concat(element.title || element.name) });
        });
    };
    if (questionNumbersMode === ShowQuestionNumbers.ON_PAGE) {
        return __assign(__assign({}, data), { pages: data.pages.map(function (page) { return (__assign(__assign({}, page), { elements: applyNumbersToElements(page.elements, (function () {
                    var pageCount = 0;
                    return function () {
                        pageCount += 1;
                        return pageCount;
                    };
                })()) })); }) });
    }
    var currentCount = 0;
    return __assign(__assign({}, data), { pages: data.pages.map(function (page) { return (__assign(__assign({}, page), { elements: applyNumbersToElements(page.elements, function () {
                currentCount += 1;
                return currentCount;
            }) })); }) });
};
export var handleApplyPageNumbers = function (data) {
    return __assign(__assign({}, data), { pages: data.pages.map(function (page, index) {
            if (!page.title) {
                return page;
            }
            return __assign(__assign({}, page), { title: "".concat(index + 1, ". ").concat(page.title) });
        }) });
};
export var extractFieldsNamesFromExpression = function (expression) {
    var match = expression.match(/\{([^}]+)\}/g) || [];
    var questionsNames = match.map(function (fragment) {
        var _a;
        var finalFragment = fragment.slice(1, -1).trim();
        if (finalFragment.includes(MATRIX_SEPARATOR_CHARACTER)) {
            var firstPart = (_a = finalFragment.split(MATRIX_SEPARATOR_CHARACTER).at(0)) !== null && _a !== void 0 ? _a : "";
            if (firstPart.includes("[") && firstPart.includes("]")) {
                return firstPart.split("[")[0];
            }
            return firstPart;
        }
        return finalFragment;
    });
    return Array.from(new Set(questionsNames)).filter(Boolean);
};
export var buildCalculatedValuesMap = function (calculatedValues, formValues) {
    if (!(calculatedValues === null || calculatedValues === void 0 ? void 0 : calculatedValues.length)) {
        return {};
    }
    var result = {};
    calculatedValues.forEach(function (calculatedValue) {
        result[calculatedValue.name] = evaluateValueExpression(calculatedValue.expression, __assign(__assign({}, formValues), result));
    });
    return result;
};
var EXPRESSION_UNSET_PRECISION = -1;
export var roundExpressionValue = function (value, precision) {
    if (precision === void 0) { precision = EXPRESSION_UNSET_PRECISION; }
    if (value === Infinity || value === -Infinity) {
        return undefined;
    }
    if (precision < 0 || !isNumericValue(value)) {
        return value;
    }
    return Number.parseFloat(Number(value).toFixed(precision));
};
export var buildExpressionValuesMap = function (pages, formValues) {
    var result = {};
    pages.forEach(function (page) {
        flattenPageElements(page.elements).forEach(function (element) {
            var _a;
            if (element.type !== SurveyElementTypeEnum.EXPRESSION) {
                return;
            }
            if (!element.expression) {
                return;
            }
            var resolvedValue = (_a = evaluateValueExpression(element.expression, __assign(__assign({}, formValues), result))) !== null && _a !== void 0 ? _a : undefined;
            result[getElementValueKey(element)] = cloneTriggerValue(roundExpressionValue(resolvedValue, element.precision));
        });
    });
    return result;
};
export var resolveMatrixExpressionCell = function (column, rowValues, formValues) {
    var _a;
    if (!column.expression) {
        return undefined;
    }
    var resolvedValue = (_a = evaluateValueExpression(column.expression, __assign(__assign({}, formValues), { row: rowValues }))) !== null && _a !== void 0 ? _a : undefined;
    return cloneTriggerValue(roundExpressionValue(resolvedValue, column.precision));
};
export var injectMatrixExpressionCells = function (payload, pages, formValues) {
    var _a;
    var result = __assign({}, payload);
    for (var _i = 0, pages_3 = pages; _i < pages_3.length; _i++) {
        var page = pages_3[_i];
        var _loop_2 = function (el) {
            if (el.type !== SurveyElementTypeEnum.MATRIXDROPDOWN &&
                el.type !== SurveyElementTypeEnum.MATRIXDYNAMIC) {
                return "continue";
            }
            var columns = ((_a = el.columns) !== null && _a !== void 0 ? _a : []);
            var expressionColumns = columns.filter(function (col) { return col.cellType === CellType.EXPRESSION && !!col.expression; });
            if (expressionColumns.length === 0)
                return "continue";
            var valueKey = getElementValueKey(el);
            var matrixValue = result[valueKey];
            if (!matrixValue || typeof matrixValue !== "object")
                return "continue";
            var withCells = function (row) {
                var rowValues = (row !== null && row !== void 0 ? row : {});
                var computed = __assign({}, rowValues);
                expressionColumns.forEach(function (col) {
                    computed[col.name] = resolveMatrixExpressionCell(col, computed, formValues);
                });
                return computed;
            };
            result[valueKey] = Array.isArray(matrixValue)
                ? matrixValue.map(withCells)
                : Object.fromEntries(Object.entries(matrixValue).map(function (_a) {
                    var rowKey = _a[0], row = _a[1];
                    return [rowKey, withCells(row)];
                }));
        };
        for (var _b = 0, _c = flattenPageElements(page.elements); _b < _c.length; _b++) {
            var el = _c[_b];
            _loop_2(el);
        }
    }
    return result;
};
export var resolveSetValueExpression = function (expression) {
    var trimmed = expression.trim();
    try {
        return JSON.parse(trimmed);
    }
    catch (_a) {
        return trimmed;
    }
};
var getDefaultMatrixCellInitial = function (cellType) {
    switch (cellType) {
        case CellType.CHECKBOX:
        case CellType.TAGBOX:
            return [];
        case CellType.BOOLEAN:
            return false;
        case CellType.RATING:
            return null;
        case CellType.TEXT:
        case CellType.COMMENT:
        case CellType.RADIOGROUP:
        default:
            return "";
    }
};
export var getInitialValueForElement = function (element) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    switch (element.type) {
        case SurveyElementTypeEnum.TEXT: {
            if (element.defaultValue) {
                return element.defaultValue;
            }
            if (element.inputType === SurveyInputTypeEnum.COLOR) {
                return "#000000";
            }
            if (element.inputType === SurveyInputTypeEnum.RANGE &&
                element.min !== undefined &&
                element.max !== undefined) {
                var min = Number(element.min);
                var max = Number(element.max);
                return String((min + max) / 2);
            }
            if (element.inputType === SurveyInputTypeEnum.NUMBER) {
                if (element.defaultValue !== undefined &&
                    element.defaultValue !== null) {
                    return String(element.defaultValue);
                }
                if (element.min !== undefined && element.min !== null) {
                    return String(element.min);
                }
                return "0";
            }
            return "";
        }
        case SurveyElementTypeEnum.COMMENT:
            return (_a = element.defaultValue) !== null && _a !== void 0 ? _a : "";
        case SurveyElementTypeEnum.DROPDOWN:
        case SurveyElementTypeEnum.RADIOGROUP:
            return (_b = element.defaultValue) !== null && _b !== void 0 ? _b : "";
        case SurveyElementTypeEnum.CHECKBOX:
            return element.defaultValue || [];
        case SurveyElementTypeEnum.TAGBOX:
            return (_c = element.defaultValue) !== null && _c !== void 0 ? _c : [];
        case SurveyElementTypeEnum.BOOLEAN: {
            var hasValueTrue = element.valueTrue !== undefined;
            var hasValueFalse = element.valueFalse !== undefined;
            var hasCustomValues = hasValueTrue || hasValueFalse;
            if (hasCustomValues) {
                if (element.defaultValue !== undefined) {
                    return element.defaultValue === element.valueTrue
                        ? (_d = element.valueTrue) !== null && _d !== void 0 ? _d : true
                        : (_e = element.valueFalse) !== null && _e !== void 0 ? _e : false;
                }
                if (hasValueFalse) {
                    return element.valueFalse;
                }
                return false;
            }
            return element.defaultValue === "true";
        }
        case SurveyElementTypeEnum.EXPRESSION:
            return null;
        case SurveyElementTypeEnum.FILE:
            return [];
        case SurveyElementTypeEnum.IMAGEPICKER:
            return (_f = element.defaultValue) !== null && _f !== void 0 ? _f : (element.multiSelect ? [] : "");
        case SurveyElementTypeEnum.SIGNATUREPAD:
            return (_g = element.defaultValue) !== null && _g !== void 0 ? _g : null;
        case SurveyElementTypeEnum.RATING:
            return element.defaultValue ? Number(element.defaultValue) : null;
        case SurveyElementTypeEnum.RANKING: {
            var choices = (element.choices || []);
            var choicesFormatted = choices.map(function (choice) {
                return typeof choice === "string" ? { value: choice, text: choice } : choice;
            });
            var ordered = element.choicesOrder
                ? reorderChoices(choicesFormatted, element.choicesOrder)
                : choicesFormatted;
            return (_h = element.defaultValue) !== null && _h !== void 0 ? _h : ordered.map(function (item) { return item.value; });
        }
        case SurveyElementTypeEnum.MATRIX: {
            if (element.defaultValue &&
                Object.keys(element.defaultValue).length > 0) {
                return element.defaultValue;
            }
            var rows = (element.rows || []);
            var initial_1 = {};
            rows.forEach(function (row) {
                initial_1[typeof row === "string" ? row : row.value] = "";
            });
            return initial_1;
        }
        case SurveyElementTypeEnum.MATRIXDROPDOWN: {
            if (element.defaultValue &&
                Object.keys(element.defaultValue).length > 0) {
                return element.defaultValue;
            }
            var rows = (element.rows || []);
            var columns_1 = (element.columns || []);
            var elementCellType_1 = element.cellType || CellType.DEFAULT;
            var initial_2 = {};
            rows.forEach(function (row) {
                var rowValue = typeof row === "string" ? row : row.value;
                initial_2[rowValue] = {};
                columns_1.forEach(function (col) {
                    var columnCellType = col.cellType || elementCellType_1;
                    initial_2[rowValue][col.name] =
                        getDefaultMatrixCellInitial(columnCellType);
                });
            });
            return initial_2;
        }
        case SurveyElementTypeEnum.MATRIXDYNAMIC: {
            if (element.defaultValue &&
                Array.isArray(element.defaultValue) &&
                element.defaultValue.length > 0) {
                return element.defaultValue;
            }
            var columns = (element.columns || []);
            var elementCellType_2 = element.cellType || CellType.DEFAULT;
            var rowsCount = element.rowCount || MATRIX_DYNAMIC_DEFAULT_ROWS;
            var result = [];
            var _loop_3 = function (i) {
                var row = {};
                columns.forEach(function (col) {
                    var columnCellType = col.cellType || elementCellType_2;
                    row[col.name] = getDefaultMatrixCellInitial(columnCellType);
                });
                result.push(row);
            };
            for (var i = 0; i < rowsCount; i++) {
                _loop_3(i);
            }
            return result;
        }
        case SurveyElementTypeEnum.MULTIPLETEXT: {
            if (element.defaultValue &&
                Object.keys(element.defaultValue).length > 0) {
                return element.defaultValue;
            }
            var items = element.items || [];
            var initial_3 = {};
            items.forEach(function (item) {
                initial_3[item.name] = "";
            });
            return initial_3;
        }
        default:
            return "";
    }
};
export var findElementPageIndexByName = function (data, name) {
    var pageIndex = data.pages.findIndex(function (page) {
        return flattenPageElements(page.elements).some(function (el) { return el.name === name; });
    });
    if (pageIndex === -1) {
        return undefined;
    }
    return pageIndex;
};
export var focusElementByName = function (name) {
    var _a;
    var container = document.querySelector("[data-element-name=\"".concat(name, "\"]"));
    (_a = container === null || container === void 0 ? void 0 : container.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), [role="combobox"]:not([aria-disabled="true"]), [tabindex]:not([tabindex="-1"])')) === null || _a === void 0 ? void 0 : _a.focus();
};
export var getMatrixColumnsConditionStatus = function (_a) {
    var visibleColumns = _a.visibleColumns, formValues = _a.formValues, elementsTypeInfoByName = _a.elementsTypeInfoByName, conditionKey = _a.conditionKey, _b = _a.shouldInvertLogic, shouldInvertLogic = _b === void 0 ? false : _b;
    var record = {};
    for (var _i = 0, visibleColumns_1 = visibleColumns; _i < visibleColumns_1.length; _i++) {
        var col = visibleColumns_1[_i];
        var condition = col[conditionKey];
        if (condition !== undefined) {
            var result = verifyElementCondition(condition, formValues, elementsTypeInfoByName);
            record[col.name] = shouldInvertLogic ? !result : result;
        }
    }
    return record;
};
export var normalizeElementChoices = function (choices) {
    return (choices || []).map(function (choice) {
        if (choice !== null && typeof choice === "object") {
            var _a = choice, value = _a.value, text = _a.text;
            return { value: String(value), text: String(text !== null && text !== void 0 ? text : value) };
        }
        return { value: String(choice), text: String(choice) };
    });
};
export var buildElementDisplayChoices = function (element) {
    var _a, _b;
    if (element.type === SurveyElementTypeEnum.BOOLEAN) {
        return [
            { value: "true", text: element.labelTrue || "Sim" },
            { value: "false", text: element.labelFalse || "Não" },
        ];
    }
    if ((_a = element.rateValues) === null || _a === void 0 ? void 0 : _a.length) {
        return normalizeElementChoices(element.rateValues);
    }
    return ((_b = element.choices) === null || _b === void 0 ? void 0 : _b.length)
        ? normalizeElementChoices(element.choices)
        : undefined;
};
export var resolveChoicesDisplayValue = function (value, choices) {
    if (value === undefined || value === null) {
        return "";
    }
    var resolveText = function (item) {
        var choice = choices === null || choices === void 0 ? void 0 : choices.find(function (currentChoice) { return currentChoice.value === String(item); });
        return choice ? choice.text : String(item);
    };
    return Array.isArray(value)
        ? value.map(resolveText).join(", ")
        : resolveText(value);
};
export var cloneTriggerValue = function (value) {
    if (value === null || typeof value !== "object") {
        return value;
    }
    try {
        return structuredClone(value);
    }
    catch (_a) {
        return Array.isArray(value)
            ? __spreadArray([], value, true)
            : __assign({}, value);
    }
};
export var buildColumnTrackedSignature = function (expression, formValues) {
    if (!expression) {
        return "";
    }
    var names = extractFieldsNamesFromExpression(expression);
    if (!names.length) {
        return "";
    }
    var formattedData = names.map(function (name) {
        var value = Array.isArray(formValues[name])
            ? __spreadArray([], formValues[name], true).map(String).sort()
            : formValues[name];
        return {
            fieldName: name,
            value: value,
        };
    });
    return JSON.stringify(formattedData);
};
export var applyMatrixColumnResetToMatrixDynamic = function (current, columnName, defaultCell) {
    var arr = (Array.isArray(current) ? current : []);
    return arr.map(function (row) {
        var _a;
        return (__assign(__assign({}, (row !== null && row !== void 0 ? row : {})), (_a = {}, _a[columnName] = defaultCell, _a)));
    });
};
export var applyMatrixColumnResetToMatrixDropdown = function (current, rows, columnName, defaultCell) {
    var _a;
    var _b;
    var base = (current !== null && current !== void 0 ? current : {});
    for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
        var row = rows_1[_i];
        var rowKey = typeof row === "string" ? row : row.value;
        base[rowKey] = __assign(__assign({}, ((_b = base[rowKey]) !== null && _b !== void 0 ? _b : {})), (_a = {}, _a[columnName] = defaultCell, _a));
    }
    return base;
};
export var getMatrixVisibleColumns = function (columns, formValues, elementsTypeInfoByName) {
    return columns.filter(function (column) {
        if (column.visibleIf === undefined) {
            return true;
        }
        return verifyElementCondition(column.visibleIf, formValues, elementsTypeInfoByName);
    });
};
export var isCorrectAnswer = function (userValue, correctAnswer, elementTypeInfo) {
    if (elementTypeInfo.type === SurveyElementTypeEnum.MATRIX ||
        elementTypeInfo.type === SurveyElementTypeEnum.MULTIPLETEXT) {
        return deepCompareObjectsDatas(userValue, correctAnswer);
    }
    if (elementTypeInfo.type === SurveyElementTypeEnum.MATRIXDROPDOWN) {
        return deepCompareMatrixDropdownDatas(userValue, correctAnswer);
    }
    if (elementTypeInfo.type === SurveyElementTypeEnum.MATRIXDYNAMIC) {
        return deepCompareMatrixDynamicDatas(userValue, correctAnswer);
    }
    if (Array.isArray(correctAnswer) && Array.isArray(userValue)) {
        var isRanking = elementTypeInfo.type === SurveyElementTypeEnum.RANKING;
        return compareTwoArraysEqual({
            array1: correctAnswer,
            array2: userValue,
            orderMatter: isRanking,
        });
    }
    return String(userValue) === String(correctAnswer);
};
export var getCorrectAnswerFieldNamesForPage = function (page) {
    return flattenPageElements(page.elements)
        .filter(function (el) { return isValueProducingElement(el) && el.correctAnswer !== undefined; })
        .map(function (el) { return getElementValueKey(el); });
};
export var getCorrectAnswerMap = function (pages) {
    var map = {};
    pages.forEach(function (page) {
        flattenPageElements(page.elements).forEach(function (el) {
            if (isValueProducingElement(el) && el.correctAnswer !== undefined) {
                map[getElementValueKey(el)] = el.correctAnswer;
            }
        });
    });
    return map;
};
export var validateAndShowValidatorsErrors = function (currentPageElements, formValues, elementsTypeInfoByName, methods) {
    var validationValidatorsResult = validateValidatorsPageElements(currentPageElements, formValues, elementsTypeInfoByName);
    var validationValidatorsWithErrors = validationValidatorsResult.filter(function (validation) { return !validation.isValid && validation.errorElement; });
    if (validationValidatorsWithErrors.length === 0) {
        return false;
    }
    for (var _i = 0, validationValidatorsWithErrors_1 = validationValidatorsWithErrors; _i < validationValidatorsWithErrors_1.length; _i++) {
        var validationResult = validationValidatorsWithErrors_1[_i];
        methods.setError(validationResult.errorElement, {
            type: "manual",
            message: validationResult.errorText || "Regra de validação não atendida",
        });
    }
    return true;
};
export var deepCompareObjectsDatas = function (userValue, correctAnswer) {
    for (var _i = 0, _a = Object.keys(correctAnswer); _i < _a.length; _i++) {
        var key = _a[_i];
        var userValueKey = userValue[key];
        var correctAnswerKey = correctAnswer[key];
        if (!compareTwoValuesEquals(userValueKey, correctAnswerKey)) {
            return false;
        }
    }
    return true;
};
export var deepCompareMatrixDropdownDatas = function (userValue, correctAnswer) {
    for (var _i = 0, _a = Object.keys(correctAnswer); _i < _a.length; _i++) {
        var key = _a[_i];
        var userValueKey = userValue[key];
        var correctAnswerKey = correctAnswer[key];
        for (var _b = 0, _c = Object.keys(correctAnswerKey); _b < _c.length; _b++) {
            var insideKey = _c[_b];
            var userInsideValue = userValueKey[insideKey];
            var correctInsideValue = correctAnswerKey[insideKey];
            if (!compareTwoValuesEquals(userInsideValue, correctInsideValue)) {
                return false;
            }
        }
    }
    return true;
};
export var deepCompareMatrixDynamicDatas = function (userValue, correctAnswer) {
    if (userValue.length !== correctAnswer.length) {
        return false;
    }
    return correctAnswer.every(function (row, index) {
        return Object.keys(row).every(function (key) {
            return compareTwoValuesEquals(row[key], userValue[index][key]);
        });
    });
};
export var isMatrixCellValueEmpty = function (val) {
    if (val === null || val === undefined) {
        return true;
    }
    if (typeof val === "string" && val === "") {
        return true;
    }
    if (typeof val === "object" && val !== null) {
        if (Array.isArray(val)) {
            return val.length === 0;
        }
        var keys = Object.keys(val);
        if (keys.length === 0) {
            return true;
        }
        return false;
    }
    return false;
};
export var computeColumnTotal = function (values, totalType) {
    var nonEmpty = values.filter(function (value) { return !isMatrixCellValueEmpty(value); });
    var numeric = nonEmpty
        .map(function (value) { return parseFlexibleNumericValue(value); })
        .filter(function (number) { return !Number.isNaN(number); });
    switch (totalType) {
        case MatrixColumnTotalType.SUM:
            return numeric.reduce(function (a, b) { return a + b; }, 0);
        case MatrixColumnTotalType.COUNT:
            return nonEmpty.length;
        case MatrixColumnTotalType.MIN:
            return numeric.length ? Math.min.apply(Math, numeric) : 0;
        case MatrixColumnTotalType.MAX:
            return numeric.length ? Math.max.apply(Math, numeric) : 0;
        case MatrixColumnTotalType.AVG:
            return nonEmpty.length
                ? numeric.reduce(function (a, b) { return a + b; }, 0) / nonEmpty.length
                : 0;
        default:
            return 0;
    }
};
export var formatColumnTotalValue = function (value, totalDisplayStyle, totalCurrency) {
    if (!totalDisplayStyle) {
        return String(value);
    }
    try {
        switch (totalDisplayStyle) {
            case MatrixColumnTotalDisplayStyle.CURRENCY:
                return new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: totalCurrency || "BRL",
                }).format(value);
            case MatrixColumnTotalDisplayStyle.DECIMAL:
                return new Intl.NumberFormat("pt-BR", { style: "decimal" }).format(value);
            case MatrixColumnTotalDisplayStyle.PERCENT:
                return new Intl.NumberFormat("pt-BR", { style: "percent" }).format(value);
            default:
                return String(value);
        }
    }
    catch (_a) {
        return String(value);
    }
};
export var getCommentFieldName = function (elementName) {
    return "".concat(elementName).concat(COMMENT_FIELD_SUFFIX);
};
export var resolveCheckErrorsMode = function (value) { return value !== null && value !== void 0 ? value : CheckErrorsMode.ON_NEXT_PAGE; };
export var applyQuestionsOnPageMode = function (data) {
    if (data.questionsOnPageMode === QuestionsOnPageMode.SINGLE_PAGE) {
        return applySinglePageMode(data);
    }
    if (data.questionsOnPageMode === QuestionsOnPageMode.QUESTION_PER_PAGE) {
        return applyQuestionPerPageMode(data);
    }
    return data;
};
var applySinglePageMode = function (data) {
    var _a;
    if (data.pages.length <= 1) {
        return data;
    }
    var showPageTitles = (_a = data.showPageTitles) !== null && _a !== void 0 ? _a : true;
    var mergedElements = data.pages.map(function (page) { return ({
        type: SurveyElementTypeEnum.PANEL,
        name: page.name,
        title: showPageTitles ? page.title : "",
        description: showPageTitles ? page.description : "",
        visible: page.visible,
        visibleIf: page.visibleIf,
        readOnly: page.readOnly,
        enableIf: page.enableIf,
        requiredIf: page.requiredIf,
        elements: page.elements,
    }); });
    return __assign(__assign({}, data), { pages: [
            {
                name: "page1",
                title: "",
                description: "",
                elements: mergedElements,
            },
        ] });
};
var applyQuestionPerPageMode = function (data) {
    var newPages = [];
    data.pages.forEach(function (page) {
        page.elements.forEach(function (element, index) {
            newPages.push({
                name: "".concat(page.name, "_q").concat(index + 1),
                title: index === 0 ? page.title : "",
                description: index === 0 ? page.description : "",
                navigationTitle: page.navigationTitle,
                visible: page.visible,
                visibleIf: page.visibleIf,
                readOnly: page.readOnly,
                enableIf: page.enableIf,
                requiredIf: page.requiredIf,
                elements: [element],
            });
        });
    });
    return __assign(__assign({}, data), { pages: newPages.length > 0 ? newPages : data.pages });
};
